import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { C, Button } from "@coremonit/design-system";
import { Search, Command, Sun, Moon, Bell, Clock } from "lucide-react";
import { useExplorer } from "../context/ExplorerContext";

export function Topbar({ onOpenPalette }: { onOpenPalette: () => void }) {
  const [theme, setTheme] = useState<"dark" | "light">("dark");
  const nav = useNavigate();
  const { timeTravelAt, setTimeTravelAt } = useExplorer();

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    document.documentElement.classList.toggle("light", theme === "light");
  }, [theme]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenPalette();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onOpenPalette]);

  return (
    <header
      style={{
        height: 56, display: "flex", alignItems: "center", gap: 12,
        padding: "0 16px",
        borderBottom: `1px solid ${C.border}`,
        background: `${C.bg}dd`,
        backdropFilter: "blur(8px)",
        flexShrink: 0,
      }}
    >
      <div style={{ flex: 1, maxWidth: 520 }}>
        <button
          onClick={onOpenPalette}
          style={{
            width: "100%", display: "flex", alignItems: "center", gap: 8,
            background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 12,
            padding: "8px 12px", cursor: "pointer",
            color: C.textMuted, fontSize: 12,
          }}
        >
          <Search size={13} />
          <span style={{ flex: 1, textAlign: "left" }}>Search clusters, pods, traces, logs…</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 6px", background: C.surface, borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 9 }}>
            <Command size={10} /> K
          </span>
        </button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          title="Time travel — rewind platform to a past timestamp"
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", background: timeTravelAt ? C.purpleDim : C.surfaceHigh, border: `1px solid ${timeTravelAt ? C.borderBright : C.border}`, borderRadius: 10 }}
        >
          <Clock size={12} color={timeTravelAt ? C.purple : C.textMuted} />
          <input
            type="datetime-local"
            value={timeTravelAt ? timeTravelAt.slice(0, 16) : ""}
            onChange={(e) => setTimeTravelAt(e.target.value ? new Date(e.target.value).toISOString() : null)}
            style={{ background: "transparent", border: "none", color: C.textSub, fontSize: 10, width: 140 }}
          />
          {timeTravelAt && (
            <Button variant="ghost" size="xs" onClick={() => setTimeTravelAt(null)}>Live</Button>
          )}
        </div>
        <button
          onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
          title={theme === "dark" ? "Switch to light" : "Switch to dark"}
          style={{ width: 32, height: 32, borderRadius: 10, background: C.surfaceHigh, border: `1px solid ${C.border}`, color: C.textSub, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
        >
          {theme === "dark" ? <Sun size={13} /> : <Moon size={13} />}
        </button>
        <button
          onClick={() => nav("/alerts")}
          title="Alerts"
          style={{ position: "relative", width: 32, height: 32, borderRadius: 10, background: C.surfaceHigh, border: `1px solid ${C.border}`, color: C.textSub, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
        >
          <Bell size={13} />
          <span style={{ position: "absolute", top: 6, right: 7, width: 6, height: 6, borderRadius: "50%", background: C.red, boxShadow: `0 0 6px ${C.red}` }} />
        </button>
      </div>
    </header>
  );
}
